import { defineStore } from 'pinia';
import { ref } from 'vue';
import { DEFAULT_DATE_FORMAT, type ActivityType, type DateFormat, type UserPreferencesDto } from '@bill-pulse/shared';
import { api } from '@/lib/api';

/**
 * Préférences personnelles (format de date, désignations récurrentes…) —
 * disponibles à tout utilisateur connecté, pas seulement aux administrateurs.
 */
export const usePreferencesStore = defineStore('preferences', () => {
  const dateFormat = ref<DateFormat>(DEFAULT_DATE_FORMAT);
  const recurringDescriptions = ref<string[]>([]);
  const activityType = ref<ActivityType | null>(null);
  const autoReminders = ref(false);
  const resolved = ref(false);

  async function bootstrap(): Promise<void> {
    if (resolved.value) return;
    try {
      const preferences = await api.getPreferences();
      dateFormat.value = preferences.dateFormat;
      recurringDescriptions.value = preferences.recurringDescriptions;
      activityType.value = preferences.activityType;
      autoReminders.value = preferences.autoReminders;
    } catch {
      // Défauts déjà en place : une facture ne doit pas rester bloquée pour ça.
    } finally {
      resolved.value = true;
    }
  }

  async function update(data: Partial<UserPreferencesDto> & Omit<UserPreferencesDto, 'autoReminders'>): Promise<void> {
    const saved = await api.updatePreferences(data);
    dateFormat.value = saved.dateFormat;
    recurringDescriptions.value = saved.recurringDescriptions;
    activityType.value = saved.activityType;
    autoReminders.value = saved.autoReminders;
  }

  /** Efface l'état local à la déconnexion : la session suivante peut être un autre compte. */
  function reset(): void {
    dateFormat.value = DEFAULT_DATE_FORMAT;
    recurringDescriptions.value = [];
    activityType.value = null;
    autoReminders.value = false;
    resolved.value = false;
  }

  return { dateFormat, recurringDescriptions, activityType, autoReminders, resolved, bootstrap, update, reset };
});
